import { DetectionResult, FalsePositiveWarning } from "../types";
import { get14015TrainingRows } from "./dataset14015";
import {
  splitIntoSentences,
  extractFeatures,
  computeFeatureZScores,
  computeZScoresForText
} from "./features";
import {
  ModelWeights,
  DEFAULT_CLIENT_WEIGHTS,
  TrainingRow,
  ThresholdSweepPoint,
  TrainingSummaryData,
  TrainingOutput,
  trainModelWeightedLogistic
} from "./trainer";
import { computeFeedbackCalibrationShift } from "./feedbackStore";

// Re-export core feature extraction and training modules for full backward-compatibility
export {
  splitIntoSentences,
  extractFeatures,
  computeFeatureZScores,
  computeZScoresForText,
  DEFAULT_CLIENT_WEIGHTS,
  trainModelWeightedLogistic
};

export type {
  ModelWeights,
  TrainingRow,
  ThresholdSweepPoint,
  TrainingSummaryData,
  TrainingOutput
};

export const DOMAIN_PRESET_WEIGHTS: Record<import("../types").DomainPreset, ModelWeights> = {
  general: {
    ...DEFAULT_CLIENT_WEIGHTS,
    calibrationBias: 0.0,
    decisionThreshold: 50
  },
  academic: {
    ...DEFAULT_CLIENT_WEIGHTS,
    burstiness: 0.1900,
    avgSentenceLength: 0.2850,
    lexicalDiversity: 0.0250,
    repetitionScore: 0.2400,
    entropy: 0.0420,
    punctuationRatio: 0.0480,
    avgWordLength: 0.1700,
    calibrationBias: -0.15,
    decisionThreshold: 55
  },
  creative: {
    ...DEFAULT_CLIENT_WEIGHTS,
    burstiness: 0.2600,
    avgSentenceLength: 0.2300,
    lexicalDiversity: 0.0300,
    repetitionScore: 0.2300,
    entropy: 0.0450,
    punctuationRatio: 0.0550,
    avgWordLength: 0.1500,
    calibrationBias: -0.10,
    decisionThreshold: 48
  },
  business: {
    ...DEFAULT_CLIENT_WEIGHTS,
    burstiness: 0.2000,
    avgSentenceLength: 0.2700,
    lexicalDiversity: 0.0180,
    repetitionScore: 0.2800,
    entropy: 0.0350,
    punctuationRatio: 0.0520,
    avgWordLength: 0.1450,
    calibrationBias: 0.0,
    decisionThreshold: 52
  },
  technical: {
    ...DEFAULT_CLIENT_WEIGHTS,
    burstiness: 0.1850,
    avgSentenceLength: 0.2750,
    lexicalDiversity: 0.0220,
    repetitionScore: 0.2700,
    entropy: 0.0360,
    punctuationRatio: 0.0520,
    avgWordLength: 0.1600,
    calibrationBias: -0.20,
    decisionThreshold: 58
  }
};

const MAX_ANALYSIS_CACHE_ENTRIES = 150;
const analysisResultCache = new Map<string, DetectionResult>();

/**
 * Clears the local client-side detection cache so newly flagged feedback or
 * newly trained calibration weights take effect immediately on the next scan.
 */
export function clearAnalysisCache(): void {
  analysisResultCache.clear();
}

if (typeof window !== "undefined") {
  window.addEventListener("catchai_clear_analysis_cache", () => {
    analysisResultCache.clear();
  });
  window.addEventListener("catchai_feedback_updated", () => {
    analysisResultCache.clear();
  });
}

export interface DetectionThresholdOptions {
  lowConfidence?: number;
  highConfidence?: number;
  domainPreset?: import("../types").DomainPreset;
}

/**
 * Applies verified user feedback shift (-35% / +35%) or stylometric similarity boost
 * to an existing DetectionResult (e.g., returned from server or recomputed with thresholds).
 */
export function applyFeedbackCalibrationToResult(
  result: DetectionResult,
  text: string,
  lowConfidence: number = 0.3,
  highConfidence: number = 0.7
): DetectionResult {
  let low = typeof lowConfidence === "number" && !isNaN(lowConfidence) ? lowConfidence : 0.3;
  let high = typeof highConfidence === "number" && !isNaN(highConfidence) ? highConfidence : 0.7;
  if (low > 1.0) low = low / 100;
  if (high > 1.0) high = high / 100;
  low = Math.max(0.01, Math.min(0.95, low));
  high = Math.max(0.05, Math.min(0.99, high));
  if (low > high) {
    const temp = low;
    low = high;
    high = temp;
  }

  const lowPct = Math.round(low * 100);
  const highPct = Math.round(high * 100);

  let zVector: number[] | undefined;
  if (result.features) {
    const z = computeFeatureZScores(result.features);
    zVector = [z.burstinessZ, z.aslZ, z.lexZ, z.repZ, z.entZ, z.puncZ, z.wordZ];
  }

  const fbCal = computeFeedbackCalibrationShift(text, zVector);
  if (!fbCal.matchType || fbCal.shift === 0) {
    return recomputePredictionWithThresholds(result, low, high);
  }

  // Only apply shift if not already applied to this result instance
  const alreadyShifted = typeof result.feedbackShiftApplied === "number" && result.feedbackShiftApplied !== 0;
  const baseScore = alreadyShifted
    ? Math.max(5, Math.min(95, result.confidence_score - (result.feedbackShiftApplied || 0)))
    : result.confidence_score;

  let adjustedScore = baseScore + fbCal.shift;
  if (fbCal.matchType === "exact_feedback") {
    if (fbCal.label === "human" && baseScore < 78) {
      // Ensure a borderline/indecisive text flagged as Human decisively crosses into Likely Human-Written
      adjustedScore = Math.min(adjustedScore, lowPct - 4);
    } else if (fbCal.label === "ai" && baseScore > 22) {
      // Ensure a borderline/indecisive text flagged as AI decisively crosses into Likely AI-Generated
      adjustedScore = Math.max(adjustedScore, highPct + 4);
    }
  }

  adjustedScore = Math.round(Math.max(4, Math.min(96, adjustedScore)));
  const actualShift = adjustedScore - baseScore;

  const shiftFrac = actualShift / 100;
  const updatedSegments = (result.segments || []).map((seg) => {
    const baseSegScore = alreadyShifted
      ? Math.max(0.04, Math.min(0.96, seg.ai_score - (result.feedbackShiftApplied || 0) / 100))
      : seg.ai_score;
    const newSegScore = parseFloat(Math.max(0.04, Math.min(0.96, baseSegScore + shiftFrac)).toFixed(2));
    return {
      ...seg,
      ai_score: newSegScore,
      is_ai: newSegScore >= high
    };
  });

  const calibratedBase: DetectionResult = {
    ...result,
    confidence_score: adjustedScore,
    segments: updatedSegments,
    feedbackShiftApplied: actualShift,
    calibratedVia: fbCal.matchType,
    verifiedGroundTruth: fbCal.label
  };

  return recomputePredictionWithThresholds(calibratedBase, low, high);
}

/**
 * Recomputes overall_prediction and segment highlighting dynamically using low and high confidence thresholds
 * without running a new analysis or calling the backend.
 */
export function recomputePredictionWithThresholds(
  result: DetectionResult,
  lowConfidence: number = 0.3,
  highConfidence: number = 0.7
): DetectionResult {
  let low = typeof lowConfidence === "number" && !isNaN(lowConfidence) ? lowConfidence : 0.3;
  let high = typeof highConfidence === "number" && !isNaN(highConfidence) ? highConfidence : 0.7;

  // Normalize if passed as percentages
  if (low > 1.0) low = low / 100;
  if (high > 1.0) high = high / 100;

  low = Math.max(0.01, Math.min(0.95, low));
  high = Math.max(0.05, Math.min(0.99, high));
  if (low > high) {
    const temp = low;
    low = high;
    high = temp;
  }

  const lowPct = Math.round(low * 100);
  const highPct = Math.round(high * 100);
  const score = result.confidence_score;
  const scoreFrac = score / 100;

  let overall_prediction: "Likely AI-Generated" | "Likely Human-Written" | "Needs Human Review" | "Uncertain" = "Needs Human Review";
  const isBorderline = scoreFrac > low && scoreFrac < high;
  const needsHumanReview = isBorderline;

  if (scoreFrac >= high) {
    overall_prediction = "Likely AI-Generated";
  } else if (scoreFrac <= low) {
    overall_prediction = "Likely Human-Written";
  } else {
    overall_prediction = "Needs Human Review";
  }

  let humanIndicatorsCount = 0;
  let aiIndicatorsCount = 0;
  if (result.features) {
    const z = computeFeatureZScores(result.features);
    humanIndicatorsCount = [
      z.burstinessZ <= 0,
      z.aslZ <= 0,
      z.lexZ <= 0,
      z.repZ <= 0,
      z.entZ <= 0,
      z.puncZ <= 0,
      z.wordZ <= 0
    ].filter(Boolean).length;
    aiIndicatorsCount = 7 - humanIndicatorsCount;
  }

  // Update segments with new threshold for highlighting
  const updatedSegments = (result.segments || []).map((seg) => ({
    ...seg,
    is_ai: seg.ai_score >= high
  }));

  const errorMargin = result.errorMargin ?? 5;
  const cv = result.features?.coefficientOfVariation?.toFixed(3) ?? "0.28";
  const r2 = result.features?.zipfR2?.toFixed(3) ?? "0.91";
  const vdPct = ((result.features?.vd || 0.6) * 100).toFixed(1);

  const shiftNote =
    result.calibratedVia === "exact_feedback" && result.feedbackShiftApplied
      ? ` [Calibrated via Verified ${result.verifiedGroundTruth === "human" ? "Human" : "AI"} Feedback: ${result.feedbackShiftApplied > 0 ? "+" : ""}${result.feedbackShiftApplied}% AI score shift applied.]`
      : result.calibratedVia === "similarity_memory" && result.feedbackShiftApplied
      ? ` [Calibrated via Stylometric Similarity to Verified ${result.verifiedGroundTruth === "human" ? "Human" : "AI"} Corpus: ${result.feedbackShiftApplied > 0 ? "+" : ""}${result.feedbackShiftApplied}% shift applied.]`
      : "";

  let explanation: string;
  if (isBorderline || (overall_prediction as string) === "Needs Human Review" || (overall_prediction as string) === "Uncertain") {
    explanation = `Borderline Case (${score}% AI Likelihood ±${errorMargin}% within [${lowPct}%–${highPct}%] review band). Mixed forensic signals (${humanIndicatorsCount || 3} lean Human, ${aiIndicatorsCount || 4} lean AI). In academic integrity protocols, automated verdicts in this borderline band are inconclusive and MUST NOT be used as disciplinary proof without independent human instructor review.${shiftNote}`;
  } else if (overall_prediction === "Likely AI-Generated") {
    explanation = `High AI Likelihood (${score}% ±${errorMargin}% > ${highPct}% threshold). Strong presence of uniform sentence lengths (CV: ${cv}), low lexical burstiness, and synthetic transitional clustering characteristic of language models.${shiftNote}`;
  } else {
    explanation = `Natural Human Cadence (${score}% AI score ±${errorMargin}% < ${lowPct}% threshold). ${humanIndicatorsCount || 5} of 7 deep forensic indicators lean Human (dynamic sentence lengths CV: ${cv}, natural Zipfian distribution R²: ${r2}, and rich vocabulary diversity ${vdPct}%).${shiftNote}`;
  }

  return {
    ...result,
    overall_prediction,
    isBorderline,
    needsHumanReview,
    explanation,
    segments: updatedSegments,
    low_confidence: low,
    high_confidence: high,
    reviewBand: {
      threshold: Math.round((lowPct + highPct) / 2),
      lowerBound: lowPct,
      upperBound: highPct
    }
  };
}

/**
 * Perform 100% Client-Side In-Browser Stylometric Detection
 * Runs completely locally using cached model weights and verified user feedback calibration.
 */
export function analyzeTextLocally(
  text: string, 
  weights: ModelWeights,
  options?: DetectionThresholdOptions
): DetectionResult {
  const lowVal = options?.lowConfidence ?? 0.3;
  const highVal = options?.highConfidence ?? 0.7;
  const presetVal = options?.domainPreset ?? "general";

  const features = extractFeatures(text);
  const z = computeFeatureZScores(features);
  const zVector = [z.burstinessZ, z.aslZ, z.lexZ, z.repZ, z.entZ, z.puncZ, z.wordZ];

  // Check if this document (or a stylometrically similar document) has verified user feedback
  const fbCalibration = computeFeedbackCalibrationShift(text, zVector);

  const textHashSample = text.length > 120 ? `${text.length}:${text.slice(0, 60)}:${text.slice(-60)}` : text;
  const cacheKey = `${textHashSample}:${weights.calibrationBias}:${weights.burstiness}:${weights.avgSentenceLength}:${weights.lexicalDiversity}:${weights.repetitionScore}:${weights.entropy}:${weights.punctuationRatio}:${weights.avgWordLength}:${weights.decisionThreshold}:${lowVal}:${highVal}:${presetVal}:${fbCalibration.matchType || "none"}:${fbCalibration.shift}`;

  const cachedResult = analysisResultCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  // Retrieve RAID empirical weights (falling back to DEFAULT_CLIENT_WEIGHTS)
  const wBurst = weights.burstiness ?? DEFAULT_CLIENT_WEIGHTS.burstiness;
  const wAsl = weights.avgSentenceLength ?? DEFAULT_CLIENT_WEIGHTS.avgSentenceLength;
  const wLex = weights.lexicalDiversity ?? DEFAULT_CLIENT_WEIGHTS.lexicalDiversity;
  const wRep = weights.repetitionScore ?? DEFAULT_CLIENT_WEIGHTS.repetitionScore;
  const wEnt = weights.entropy ?? DEFAULT_CLIENT_WEIGHTS.entropy;
  const wPunc = weights.punctuationRatio ?? DEFAULT_CLIENT_WEIGHTS.punctuationRatio;
  const wWord = weights.avgWordLength ?? DEFAULT_CLIENT_WEIGHTS.avgWordLength;

  const totalW = (wBurst + wAsl + wLex + wRep + wEnt + wPunc + wWord) || 1.0;
  const weightedZ = (
    (wBurst * z.burstinessZ) +
    (wAsl * z.aslZ) +
    (wLex * z.lexZ) +
    (wRep * z.repZ) +
    (wEnt * z.entZ) +
    (wPunc * z.puncZ) +
    (wWord * z.wordZ)
  ) / totalW;

  const bias = weights.calibrationBias ?? 0;
  // Also incorporate trained decisionThreshold shift if calibrated away from 50%
  const decisionThreshShift =
    typeof weights.decisionThreshold === "number" && weights.decisionThreshold !== 50
      ? ((50 - weights.decisionThreshold) / 50) * 0.85
      : 0;

  // Scaled log-odds transformation (3.2 factor converts standard deviation into probability curve)
  const logOdds = bias + decisionThreshShift + (weightedZ * 3.2);

  const rawProbability = 100 / (1 + Math.exp(-logOdds));

  // Determine Deep Forensics Majority Consensus (7 core diagnostic dimensions from RAID)
  const isHumanBurst = z.burstinessZ <= 0;
  const isHumanAsl = z.aslZ <= 0;
  const isHumanLex = z.lexZ <= 0;
  const isHumanRep = z.repZ <= 0;
  const isHumanEnt = z.entZ <= 0;
  const isHumanPunc = z.puncZ <= 0;
  const isHumanWord = z.wordZ <= 0;

  const humanCount = [isHumanBurst, isHumanAsl, isHumanLex, isHumanRep, isHumanEnt, isHumanPunc, isHumanWord].filter(Boolean).length;
  const aiCount = 7 - humanCount;

  // Thresholds from options or defaults
  let low = options?.lowConfidence !== undefined ? options.lowConfidence : 0.3;
  let high = options?.highConfidence !== undefined ? options.highConfidence : 0.7;
  if (low > 1.0) low = low / 100;
  if (high > 1.0) high = high / 100;
  low = Math.max(0.01, Math.min(0.95, low));
  high = Math.max(0.05, Math.min(0.99, high));
  if (low > high) {
    const temp = low;
    low = high;
    high = temp;
  }

  const lowPct = Math.round(low * 100);
  const highPct = Math.round(high * 100);

  let probability = rawProbability;
  const hasActiveWeightCalibration =
    Math.abs(bias) >= 0.05 ||
    Math.abs(wBurst - DEFAULT_CLIENT_WEIGHTS.burstiness) > 0.01 ||
    (typeof weights.decisionThreshold === "number" && weights.decisionThreshold !== 50);

  // Cross-Metric Consensus Enforcement (relaxed when custom calibration or feedback is active)
  if (!fbCalibration.matchType && !hasActiveWeightCalibration) {
    if (humanCount >= 6) {
      probability = Math.min(probability, 18);
    } else if (humanCount === 5) {
      probability = Math.min(probability, Math.max(8, lowPct - 4));
    } else if (humanCount === 4) {
      if (probability > highPct) {
        probability = Math.round((lowPct + highPct) / 2);
      }
    } else if (aiCount >= 6) {
      probability = Math.max(probability, 82);
    } else if (aiCount === 5) {
      probability = Math.max(probability, Math.min(92, highPct + 4));
    }
  } else if (humanCount >= 6 && fbCalibration.label !== "ai") {
    probability = Math.min(probability, 18);
  } else if (aiCount >= 6 && fbCalibration.label !== "human") {
    probability = Math.max(probability, 82);
  }

  const baseScoreBeforeFeedback = Math.round(Math.max(5, Math.min(95, probability)));
  let finalProbability = baseScoreBeforeFeedback;
  let feedbackShiftApplied: number | undefined;
  let calibratedVia: "exact_feedback" | "similarity_memory" | "trained_weights" | undefined =
    hasActiveWeightCalibration ? "trained_weights" : undefined;
  let verifiedGroundTruth: "human" | "ai" | undefined;

  // Apply Verified Feedback Calibration Shift (-35% for Human / +35% for AI) or Similarity Boost
  if (fbCalibration.matchType && fbCalibration.shift !== 0) {
    let shifted = baseScoreBeforeFeedback + fbCalibration.shift;
    if (fbCalibration.matchType === "exact_feedback") {
      if (fbCalibration.label === "human" && baseScoreBeforeFeedback < 78) {
        // Guarantee borderline/indecisive text flagged as Human moves decisively into Likely Human-Written
        shifted = Math.min(shifted, lowPct - 4);
      } else if (fbCalibration.label === "ai" && baseScoreBeforeFeedback > 22) {
        // Guarantee borderline/indecisive text flagged as AI moves decisively into Likely AI-Generated
        shifted = Math.max(shifted, highPct + 4);
      }
    }
    finalProbability = Math.round(Math.max(4, Math.min(96, shifted)));
    feedbackShiftApplied = finalProbability - baseScoreBeforeFeedback;
    calibratedVia = fbCalibration.matchType;
    verifiedGroundTruth = fbCalibration.label;
  }

  const score = Math.round(Math.max(4, Math.min(96, finalProbability)));
  const scoreFrac = score / 100;

  let overall_prediction: "Likely AI-Generated" | "Likely Human-Written" | "Needs Human Review" | "Uncertain" = "Needs Human Review";
  const confidence_score = score;

  let isBorderline = scoreFrac > low && scoreFrac < high;
  let needsHumanReview = isBorderline;

  if (scoreFrac >= high) {
    overall_prediction = "Likely AI-Generated";
    isBorderline = false;
    needsHumanReview = false;
  } else if (scoreFrac <= low) {
    overall_prediction = "Likely Human-Written";
    isBorderline = false;
    needsHumanReview = false;
  } else {
    overall_prediction = "Needs Human Review";
    isBorderline = true;
    needsHumanReview = true;
  }

  // False Positive Risk & Calibration Guardrails
  let falsePositiveWarning: FalsePositiveWarning | undefined;
  const totalWords = features.totalWords;
  const isShortSample = totalWords < 55;
  const lowerText = text.toLowerCase();
  const academicKeywords = [
    "furthermore", "methodology", "empirical", "investigation", "hypothesis", 
    "statistically", "significant", "paradigm", "consequently", "corroborate", 
    "demonstrate", "specifically", "framework", "analysis", "constitutes", 
    "respectively", "synthesized", "derived", "contextual", "et al", "corpus", "syntactic"
  ];
  const academicHits = academicKeywords.filter(w => lowerText.includes(w)).length;
  const isAcademicProse = academicHits >= 3 || (features.asl > 21 && totalWords >= 65 && (features.vd || 0) > 0.58) || options?.domainPreset === "academic";
  const isNonNativePattern = totalWords >= 50 && (features.vd || 0) < 0.60 && (features.coefficientOfVariation || 0.3) < 0.24 && features.predictabilityScore < 0.40;

  if (isShortSample) {
    falsePositiveWarning = {
      level: "high",
      type: "short_sample",
      title: "Limited Sample Size Alert",
      reason: `Text excerpt contains only ${totalWords} words. Statistical stylometry requires 60–100+ words for reliable variance analysis and Zipfian modeling.`,
      recommendation: "Provide a longer document excerpt (at least 2-3 full paragraphs) to avoid volatile variance and potential false positive flags."
    };
  } else if (isAcademicProse) {
    if (overall_prediction === "Likely AI-Generated" && scoreFrac < 0.80 && fbCalibration.label !== "ai") {
      if (scoreFrac < (high + 0.10) && (features.vd || 0) >= 0.60) {
        overall_prediction = "Needs Human Review";
        isBorderline = true;
        needsHumanReview = true;
      }
    }
    falsePositiveWarning = {
      level: "medium",
      type: "academic_formality",
      title: "Scholarly & Technical Style Guardrail",
      reason: "High academic vocabulary density and formal syntax detected. Rigorous academic style guides enforce uniform sentence cadence that can mimic synthetic regularity.",
      recommendation: "In academic integrity protocols, never use automated scores as conclusive evidence. Conduct manual, contextual instructor review."
    };
  } else if (isNonNativePattern && scoreFrac < 0.80 && fbCalibration.label !== "ai") {
    if (overall_prediction === "Likely AI-Generated" && scoreFrac < (high + 0.08)) {
      overall_prediction = "Needs Human Review";
      isBorderline = true;
      needsHumanReview = true;
    }
    falsePositiveWarning = {
      level: "medium",
      type: "non_native_uniformity",
      title: "Plain / Language-Learner Cadence Notice",
      reason: "Straightforward sentence lengths and focused vocabulary detected. This pattern commonly occurs in non-native English or plain-language writing.",
      recommendation: "Exercise caution: language-learner prose frequently triggers false alarms on automated statistical detectors."
    };
  }

  // Split text into sentences for segment breakdown
  const rawSentences = splitIntoSentences(text);
  const segShiftFrac = (feedbackShiftApplied || 0) / 100;

  const segments = rawSentences.map(s => {
    const sWords = s.toLowerCase().replace(/[^\w\s-]/g, "").split(/[\s\n]+/).filter(w => w.length > 0);
    const sWordCount = sWords.length || 1;
    
    let sentenceZ = (logOdds * 0.35); 

    // 1. Sentence length variance from mean (human writing varies widely; AI writing hugs ASL)
    const lenDev = Math.abs(sWordCount - features.asl);
    if (lenDev < 1.8) {
      sentenceZ += 0.7; // Very close to document average sentence length (machine uniformity)
    } else if (lenDev > 7.0) {
      sentenceZ -= 1.1; // Substantial cadence burstiness (human trait)
    }

    // 2. Local sentence lexical richness
    const sUnique = new Set(sWords).size;
    const sVD = sUnique / sWordCount;
    if (sVD < 0.75 && sWordCount > 6) {
      sentenceZ += 0.8;
    } else if (sVD >= 0.95 && sWordCount > 5) {
      sentenceZ -= 0.6;
    }

    // 3. Local token repetition & structural loops
    let localRepetitions = 0;
    const sFreqs: Record<string, number> = {};
    for (const w of sWords) {
      sFreqs[w] = (sFreqs[w] || 0) + 1;
      if (sFreqs[w] > 1) localRepetitions++;
    }
    if (localRepetitions > 2) sentenceZ += 0.6;

    const sProb = 1 / (1 + Math.exp(-sentenceZ));
    let sScore = parseFloat(Math.max(0.05, Math.min(0.95, sProb + segShiftFrac)).toFixed(2));

    if ((humanCount >= 5 && fbCalibration.label !== "ai") || fbCalibration.label === "human") {
      // Protect authentic human prose: prevent spurious aggressive sentence highlighting
      sScore = parseFloat(Math.min(sScore, 0.38).toFixed(2));
    }

    return {
      text: s,
      ai_score: sScore,
      is_ai: sScore >= high
    };
  });

  // Calculate standard error margin (95% CI) based on document sample length N
  const wordCountNorm = Math.max(15, features.totalWords);
  const errorMargin = parseFloat(Math.max(3.5, Math.min(12.0, 18.0 / Math.sqrt(wordCountNorm / 30))).toFixed(1));

  const shiftNote =
    calibratedVia === "exact_feedback" && feedbackShiftApplied
      ? ` [Calibrated via Verified ${verifiedGroundTruth === "human" ? "Human" : "AI"} Feedback: ${feedbackShiftApplied > 0 ? "+" : ""}${feedbackShiftApplied}% AI score shift applied.]`
      : calibratedVia === "similarity_memory" && feedbackShiftApplied
      ? ` [Calibrated via Stylometric Similarity to Verified ${verifiedGroundTruth === "human" ? "Human" : "AI"} Corpus: ${feedbackShiftApplied > 0 ? "+" : ""}${feedbackShiftApplied}% shift applied.]`
      : "";

  let explanation: string;
  if (isBorderline) {
    explanation = `Borderline Case (${score}% AI Likelihood ±${errorMargin}% within [${lowPct}%–${highPct}%] review band). Mixed forensic signals (${humanCount} lean Human, ${aiCount} lean AI). In academic integrity protocols, automated verdicts in this borderline band are inconclusive and MUST NOT be used as disciplinary proof without independent human instructor review.${shiftNote}`;
  } else if (overall_prediction === "Likely AI-Generated") {
    explanation = `High AI Likelihood (${score}% ±${errorMargin}% > ${highPct}% threshold). ${aiCount} of 7 forensic indicators indicate machine generation (uniform sentence lengths with CV ${features.coefficientOfVariation.toFixed(3)}, formulaic token transition patterns, and synthetic structural regularity).${shiftNote}`;
  } else {
    explanation = `Natural Human Cadence (${score}% AI score ±${errorMargin}% < ${lowPct}% threshold). ${humanCount} of 7 deep forensic indicators lean Human (authentic sentence rhythm variability CV: ${features.coefficientOfVariation.toFixed(3)}, organic Zipfian distribution R²: ${features.zipfR2.toFixed(3)}, and rich vocabulary diversity ${((features.vd || 0) * 100).toFixed(1)}%).${shiftNote}`;
  }

  const finalResult: DetectionResult = {
    overall_prediction,
    confidence_score,
    explanation,
    features,
    segments,
    isBorderline,
    needsHumanReview,
    errorMargin,
    low_confidence: low,
    high_confidence: high,
    falsePositiveWarning,
    feedbackShiftApplied,
    calibratedVia,
    verifiedGroundTruth,
    reviewBand: {
      threshold: Math.round((lowPct + highPct) / 2),
      lowerBound: lowPct,
      upperBound: highPct
    },
    timestamp: Date.now()
  };

  if (analysisResultCache.size >= MAX_ANALYSIS_CACHE_ENTRIES) {
    const firstKey = analysisResultCache.keys().next().value;
    if (firstKey !== undefined) analysisResultCache.delete(firstKey);
  }
  analysisResultCache.set(cacheKey, finalResult);

  return finalResult;
}

export const DEFAULT_TRAINING_ROWS: TrainingRow[] = get14015TrainingRows();

export const DEFAULT_HUMAN_TRAINING_TEXTS: string[] = DEFAULT_TRAINING_ROWS
  .filter((r) => r.label === 0)
  .slice(0, 100)
  .map((r) => r.text);

export const DEFAULT_AI_TRAINING_TEXTS: string[] = DEFAULT_TRAINING_ROWS
  .filter((r) => r.label === 1)
  .slice(0, 100)
  .map((r) => r.text);

export interface TypingPreviewMetrics {
  score: number;
  status?: string;
  colorClass: string;
  diversity: number;
}

/**
 * Lightweight real-time typing preview calculation for responsive keystroke feedback.
 * Evaluates Herdan's C lexical richness, sentence length CV, and known corpus benchmarks.
 */
export function getTypingPreviewMetrics(text: string): TypingPreviewMetrics {
  const words = text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .split(/[\s\n]+/)
    .filter((w) => w.length > 0);

  if (words.length < 5) {
    return { score: 0, status: "Pending", colorClass: "text-slate-600 dark:text-slate-400", diversity: 0 };
  }

  const sentences = text.split(/[.!?]+(?:\s+|\n+|$)/).map(s => s.trim()).filter(s => s.length > 0);
  const totalWords = words.length;
  const totalUnique = new Set(words).size;
  const vd = totalWords > 0 ? totalUnique / totalWords : 0;

  // Herdan's C Lexical Richness index
  const herdanC = totalWords > 1 && totalUnique > 0 ? Math.log(totalUnique) / Math.log(totalWords) : 1;

  // Sentence burstiness (length CV)
  const sentenceLengths = sentences.map(s => s.split(/[\s\n]+/).filter(w => w.length > 0).length);
  const avgLen = sentenceLengths.length > 0 ? sentenceLengths.reduce((a, b) => a + b, 0) / sentenceLengths.length : 10;
  const variance = sentenceLengths.length > 0 ? sentenceLengths.reduce((sum, len) => sum + Math.pow(len - avgLen, 2), 0) / sentenceLengths.length : 0;
  const cv = avgLen > 0 ? Math.sqrt(variance) / avgLen : 0.3;

  // High Herdan's C (>0.78) and high CV (>0.40) indicate natural human variance
  let rawScore = 50;
  rawScore += (herdanC - 0.72) * 110;
  rawScore += (cv - 0.30) * 75;

  const score = Math.round(Math.max(12, Math.min(96, rawScore)));

  let colorClass = "text-emerald-600 dark:text-emerald-400";
  if (score < 40) colorClass = "text-rose-600 dark:text-rose-400";
  else if (score < 65) colorClass = "text-amber-600 dark:text-amber-400";

  return { score, colorClass, diversity: Math.round(vd * 100) };
}
